import { useTranslation } from 'hooks/api/useTranslation';
import { BaseCancellationBlockedModal } from './baseCancellationBlockedModal/baseCancellationBlockedModal';
import { CancelSubscriptionModal } from 'pages/subscriptions/ui/subscriptions/subscriptions-table/cancelSubscriptionModal';
import { ResumeModal } from 'pages/subscriptions/ui/subscriptions/subscriptions-table/resumeSubscriptionModal';
import { FC, useState } from 'react';
import { Link } from 'react-router';
import { Filter } from 'shared/constants/color-filters';
import { ICONS } from 'shared/constants/images';
import { formatDate } from 'shared/helpers/format-date';
import { formatMonthlyPrice } from 'shared/helpers/formatMonthlyPrice';
import { snackbar } from 'shared/helpers/snackbar';
import Button from 'shared/ui/button/button';
import { Icon } from 'shared/ui/icon/icon';
import { useGetBillingSpendQuery } from 'store/api/apiSlice';
import { SubscriptionEntry, SubscriptionState } from 'store/api/apiTypes';
import {
  useCancelLicenseMutation,
  useResumeLicenseMutation,
  useTerminateLicenseMutation,
} from 'store/api/userApiSlice';
import { track } from 'utils/mixpanel';
import cls from './subscription-actions-cell.module.scss';
import { useCachedUserType } from 'hooks';
import {
  ADD_ON_PLAN,
  BASE_PLAN,
  planOf,
  SubscriptionActionsCellProps,
} from '../subscription-actions-cell/subscription-actions-cell.type';

const isActiveAddOn = (entry: SubscriptionEntry): boolean =>
  planOf(entry) === ADD_ON_PLAN &&
  entry.license.state !== SubscriptionState.TERMINATED &&
  entry.license.state !== SubscriptionState.CANCELING;

const isCancellingBase = (entry: SubscriptionEntry): boolean =>
  planOf(entry) === BASE_PLAN &&
  entry.license.state === SubscriptionState.CANCELING;

const getBlockingAddOns = (
  entry: SubscriptionEntry,
  license: SubscriptionEntry[]
): SubscriptionEntry[] =>
  planOf(entry) === BASE_PLAN ? license.filter(isActiveAddOn) : [];

export const SubscriptionActionsCell: FC<SubscriptionActionsCellProps> = ({
  license,
  allSubscriptions = [],
  onNavigateToWebsites,
}) => {
  const { t } = useTranslation();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);
  const [isResumeBlockedModalOpen, setIsResumeBlockedModalOpen] =
    useState(false);
  const [terminateLicense] = useTerminateLicenseMutation();
  const [cancelLicense] = useCancelLicenseMutation();
  const [resumeLicense] = useResumeLicenseMutation();

  const { isDirect } = useCachedUserType();
  const isCancelling =
    isDirect && license.license.state === SubscriptionState.CANCELING;
  const isUnassigned = isDirect && !isCancelling && !license.domain?.name;

  const { data: billingSpend } = useGetBillingSpendQuery(undefined, {
    skip: !isCancelling,
  });

  // The base/add-on model is a Riva (direct) concept; the ka flow hands the
  // customer over to the partner and is out of scope for this rule.
  const blockingAddOns = isDirect
    ? getBlockingAddOns(license, allSubscriptions)
    : [];
  const isBaseCancellationBlocked = blockingAddOns.length > 0;
  const isAddOnResumeBlocked =
    isDirect &&
    planOf(license) === ADD_ON_PLAN &&
    allSubscriptions.some(isCancellingBase);

  const openResumeFlow = () => {
    if (isAddOnResumeBlocked) {
      setIsResumeBlockedModalOpen(true);
      return;
    }
    setIsResumeModalOpen(true);
  };

  const openCancelFlow = () => {
    // AC: the block is applied when the flow is entered, not only by hiding a
    // button, so reaching the cancel dialogue at all is impossible here.
    if (isBaseCancellationBlocked) {
      setIsBlockedModalOpen(true);
      return;
    }
    setIsCancelModalOpen(true);
  };

  const renderCancelDescription = () => {
    const template = t('cancelSubscriptionDesc');
    const dateStr = formatDate(
      new Date(license.license.expiration_date),
      'long'
    );
    const [before, after] = template.split('{date}');
    return (
      <>
        {before}
        <b>{dateStr}</b>
        {after}
      </>
    );
  };

  const renderKaDescription = () =>
    license.domain?.name
      ? t('cancelSubscriptionDescKaAssigned', '_global', '', {
          domain: license.domain.name,
        })
      : t('cancelSubscriptionDescKa');

  const nextBillingDateStr = formatDate(
    new Date(
      license.license.next_billing_date ?? license.license.expiration_date
    ),
    'long'
  );
  const spendEntry = billingSpend?.websites.find(
    w => String(w.license_id) === String(license.license.id)
  );
  const resumeAmountStr = spendEntry
    ? formatMonthlyPrice(
        spendEntry.monthly_price,
        spendEntry.currency,
        t('month')
      )
    : '';

  const handleCancelSubscription = async () => {
    track('AIS:LICENSE_MGMT-CANCEL_SUBSCRIPTION-CLICKED');
    // Re-checked right before the request: the list can change while the
    // dialogue is open, and this is the last point the client controls.
    if (isBaseCancellationBlocked) {
      setIsCancelModalOpen(false);
      setIsBlockedModalOpen(true);
      return;
    }
    try {
      if (isDirect) {
        await cancelLicense({ license_id: license.license.id }).unwrap();
        setIsCancelModalOpen(false);
        snackbar(t('cancelSubscriptionSuccessStandalone'));
      } else {
        await terminateLicense({
          domain: license.domain?.name ?? '',
          license_id: license.license.id,
        }).unwrap();
        setIsCancelModalOpen(false);
        snackbar(t('cancelSubscriptionSuccess'));
      }
    } catch (error) {
      snackbar(t('cancelSubscriptionError'));
    }
  };

  const handleResumeSubscription = async () => {
    if (isAddOnResumeBlocked) {
      setIsResumeModalOpen(false);
      setIsResumeBlockedModalOpen(true);
      return;
    }
    try {
      await resumeLicense({ license_id: license.license.id }).unwrap();
      setIsResumeModalOpen(false);
      snackbar(t('resumeSubscriptionSuccess'));
    } catch (error) {
      snackbar(t('resumeSubscriptionError'));
    }
  };

  return (
    <>
      <div className={cls.actionsCell}>
        {isDirect ? (
          <>
            <Button
              theme={isCancelling ? 'tonal' : 'text'}
              tone='default'
              onClick={isCancelling ? openResumeFlow : openCancelFlow}
            >
              {isCancelling ? t('resumeSubscription') : t('cancel')}
            </Button>
            {isUnassigned && (
              <Button
                theme='tonal'
                tone='default'
                onClick={onNavigateToWebsites}
              >
                {t('assignAWebsite')}
              </Button>
            )}
          </>
        ) : (
          <Link to='#' className={cls.cancelLink} onClick={openCancelFlow}>
            <Icon pathToIcon={ICONS.newTab} filter={Filter.primary} />
            <span>{t('cancelLicense')}</span>
          </Link>
        )}
      </div>
      {isCancelModalOpen && (
        <CancelSubscriptionModal
          isOpen={isCancelModalOpen}
          description={
            isDirect ? renderCancelDescription() : renderKaDescription()
          }
          cancelText={isDirect ? t('keepSubscription') : t('cancel')}
          action={isDirect ? t('cancelLicense') : t('continueToPartner')}
          onClose={() => setIsCancelModalOpen(false)}
          onClickAction={() => handleCancelSubscription()}
          onClickCancel={() => setIsCancelModalOpen(false)}
        />
      )}
      {isBlockedModalOpen && (
        <BaseCancellationBlockedModal
          isOpen={isBlockedModalOpen}
          heading={t('baseCancellationBlockedHeading')}
          reason={t('baseCancellationBlockedReason')}
          onClose={() => setIsBlockedModalOpen(false)}
          onManageLicenses={() => {
            setIsBlockedModalOpen(false);
            onNavigateToWebsites();
          }}
        />
      )}
      {isResumeBlockedModalOpen && (
        <BaseCancellationBlockedModal
          isOpen={isResumeBlockedModalOpen}
          heading={t('addOnResumeBlockedHeading')}
          reason={t('addOnResumeBlockedReason')}
          onClose={() => setIsResumeBlockedModalOpen(false)}
        />
      )}
      {isResumeModalOpen && (
        <ResumeModal
          isOpen={isResumeModalOpen}
          nextBillingDate={nextBillingDateStr}
          amount={resumeAmountStr}
          onClose={() => setIsResumeModalOpen(false)}
          onClickResume={() => handleResumeSubscription()}
          onClickCancel={() => setIsResumeModalOpen(false)}
        />
      )}
    </>
  );
};
